import { IconName } from '@fortawesome/fontawesome-svg-core';
import { FormatConfig } from '@y42/model';

export class DatosAxisModel {
  constructor(
    data: DatosAxisModel | null = null,
    type: AxisTypeEnum | null = null,
    axisAlign: TextAlignEnum | undefined = undefined,
  ) {
    if (data) {
      this.show = data.show;
      this.type = data.type;
      this.axisName = data.axisName;
      this.axisLine = data.axisLine;
      this.axisLabel = { ...data.axisLabel };
      this.splitLine = data.splitLine;
      this.dataZoom = new DatosDataZoomDetails(data.dataZoom);
    } else {
      this.show = true;
      this.type = type;
      this.axisName = new DatosAxisName();
      this.axisLine = new DatosAxisLine();
      this.axisLabel = new DatosAxisLabel(axisAlign);
      this.splitLine = new DatosSplitLine();
      this.dataZoom = new DatosDataZoomDetails();
    }
  }
  show: boolean;
  type: AxisTypeEnum | null;
  axisName: DatosAxisName;
  axisLine: DatosAxisLine;
  axisLabel: DatosAxisLabel;
  splitLine: DatosSplitLine;
  dataZoom: DatosDataZoomDetails;

  // only for type === 'value'
  min: number | null = null;
  max: number | null = null;
}

export class DatosAxisName {
  name: string | undefined = undefined;
  nameRotate = 0;
  nameGap = 10;
  nameLocation: NameLocationEnum = NameLocationEnum.middle;
  nameTextStyle: DatosTextStyle = new DatosTextStyle();
}

export class DatosAxisLine {
  showLabel = true;
  symbol: AxisSymbolEnum = AxisSymbolEnum.none;
  lineStyle: DatosLineStyle = new DatosLineStyle();
}

export class DatosSplitLine {
  constructor(public showLabel = false, public lineStyle: DatosLineStyle = new DatosLineStyle()) {}
}

export class DatosDataZoomDetails {
  constructor(data: DatosDataZoomDetails | null = null, isDimension = false) {
    if (data) {
      this.show = data.show;
      this.start = data.start;
      this.end = data.end;
    } else {
      this.show = isDimension;
    }
  }
  show = false;
  start = 0;
  end = 100;
}

export class DatosLineStyle {
  constructor(public type: LineStyleEnum = LineStyleEnum.solid, public opacity = 1) {}

  static getLightStyle(line_type = 'dotted') {
    return {
      opacity: 0.15,
      type: line_type,
    } as DatosLineStyle;
  }
}

// toDo combine it with theme in future;
// public color: string = '#a7a7a7',
// getLightStyle -> color: '#000',

export class DatosTextStyleWithoutColors {
  constructor(
    public backgroundColor = 'transparent',
    public borderColor = '#f4f4fb',
    public borderWidth = 0,
    public fontSize = 12,
    public align: TextAlignEnum = TextAlignEnum.center,
    public fontStyle: FontStyleEnum = FontStyleEnum.normal,
    public fontWeight: FontWeightEnum = FontWeightEnum.normal,
    public verticalAlign: VerticalAlignEnum = VerticalAlignEnum.middle,
    public fontFamily = 'Arial',

    public borderRadius = 0,
    public padding: number[] = [2, 2, 2, 2],
  ) {}
}

export class DatosTextStyle extends DatosTextStyleWithoutColors {
  constructor(
    public color = '#a7a7a7',
    public backgroundColor = 'transparent',
    public borderColor = '#f4f4fb',
    public borderWidth = 0,
    public fontSize = 12,
    public align: TextAlignEnum = TextAlignEnum.center,
    public colorTo = '#a7a7a7',
  ) {
    super(backgroundColor, borderColor, borderWidth);
  }
}

export class RichTextStyle extends DatosTextStyle {
  constructor(
    public borderPosition = BorderPositionEnum.noneBorder,
    public wordBreak = WordBreakEnum.keepAll,
    public backgroundColor = 'transparent',
    public color = '#a7a7a7',
    public borderColor = '#000000',
    public borderWidth = 1,
    public fontSize = 12,
    public align: TextAlignEnum = TextAlignEnum.center,
    public activeStyles = false,
  ) {
    super(color, backgroundColor, borderColor, borderWidth, fontSize, align);
  }
}

export class BorderSettings {
  constructor(
    public borderColor = '#f4f4fb',
    public borderWidth = 0,
    public borderPosition = BorderPositionEnum.noneBorder,
  ) {}
}

export class DatosAxisLabel extends DatosTextStyleWithoutColors {
  constructor(
    public align: TextAlignEnum = TextAlignEnum.center,
    public show = true,
    public rotate = 0,
    public margin = 22,
    public suffix = '',
    public max_characters: number | null = null,
    public value_conversion: '' | 'K' | 'M' | 'B' | 'Automatic' = 'Automatic',
    public date_format = '',
    public value_format = 'inherit',
    public value_format_config?: FormatConfig,
    public formatter = (value: unknown) => {
      if (this.max_characters && `${value}`.length > this.max_characters) {
        return `${(value + (this.suffix ? this.suffix : '')).toString().slice(0, this.max_characters)}...`;
      }
      return value + (this.suffix ? this.suffix : '');
    },
  ) {
    super(undefined, undefined, undefined, undefined, align);
  }
}

export class DatosAxisPointer {
  show = false;
  type: string = AxisPointerEnum.line;
  lineStyle: DatosLineStyle = new DatosLineStyle();
}

export enum AxisSymbolEnum {
  none = 'none',
  arrow = 'arrow',
}

export enum FontStyleEnum {
  normal = 'normal',
  italic = 'italic',
  oblique = 'oblique',
}

export enum FontWeightEnum {
  normal = 'normal',
  bold = 'bold',
}

export enum VerticalAlignEnum {
  top = 'top',
  middle = 'middle',
  bottom = 'bottom',
}

export enum NameLocationEnum {
  start = 'start',
  middle = 'middle',
  end = 'end',
}

export enum AxisTypeEnum {
  value = 'value',
  category = 'category',
  time = 'time',
  log = 'log',
}

export enum TextAlignEnum {
  left = 'left',
  center = 'center',
  right = 'right',
}

export enum JustifyContentEnum {
  flexStart = 'flex-start',
  center = 'center',
  flexEnd = 'flex-end',
}

export const FlexHorizontalAlign: Record<string, JustifyContentEnum> = {
  left: JustifyContentEnum.flexStart,
  center: JustifyContentEnum.center,
  right: JustifyContentEnum.flexEnd,
};

export const FlexVerticalAlign: Record<string, JustifyContentEnum> = {
  top: JustifyContentEnum.flexStart,
  middle: JustifyContentEnum.center,
  bottom: JustifyContentEnum.flexEnd,
};

export enum BorderPositionEnum {
  fullBorder = 'fullBorder',
  inside = 'inside',
  horizontalBorder = 'horizontalBorder',
  verticalBorder = 'verticalBorder',
  border = 'border',
  leftBorder = 'leftBorder',
  topBorder = 'topBorder',
  rightBorder = 'rightBorder',
  bottomBorder = 'bottomBorder',
  noneBorder = 'noneBorder',
}

export enum WordBreakEnum {
  normal = 'normal',
  breakAll = 'breakAll',
  keepAll = 'keepAll',
}

export enum LineStyleEnum {
  solid = 'solid',
  dashed = 'dashed',
  dotted = 'dotted',
}

export enum AxisPointerEnum {
  line = 'line',
  shadow = 'shadow',
  none = 'none',
}

/** @deprecated - there is also an enum LabelPosition, use only one */
export enum LabelPositionEnum {
  top = 'top',
  left = 'left',
  right = 'right',
  bottom = 'bottom',
  inside = 'inside',
  insideLeft = 'insideLeft',
  insideRight = 'insideRight',
  insideTop = 'insideTop',
  insideBottom = 'insideBottom',
  insideTopLeft = 'insideTopLeft',
  insideBottomLeft = 'insideBottomLeft',
  insideTopRight = 'insideTopRight',
  insideBottomRight = 'insideBottomRight',
}

export class AxisStyleModel {
  constructor(data: Record<string, string>) {
    this.name = data.name as TextAlignEnum;
    this.icon = data.icon as IconName;
  }
  name: TextAlignEnum;
  icon: IconName;
}

export const TextAlignTypes = [
  new AxisStyleModel({
    name: TextAlignEnum.left,
    icon: 'align-left',
  }),
  new AxisStyleModel({
    name: TextAlignEnum.center,
    icon: 'align-center',
  }),
  new AxisStyleModel({
    name: TextAlignEnum.right,
    icon: 'align-right',
  }),
];

export const FontWeightTypes = [
  new AxisStyleModel({
    name: FontWeightEnum.bold,
    icon: 'bold',
  }),
];

export const VerticalAlignTypes = [
  new AxisStyleModel({
    name: VerticalAlignEnum.bottom,
    icon: 'arrow-down',
  }),
  new AxisStyleModel({
    name: VerticalAlignEnum.middle,
    icon: 'arrows-alt-v',
  }),
  new AxisStyleModel({
    name: VerticalAlignEnum.top,
    icon: 'arrow-up',
  }),
];

export const LineStyleTypes = [
  new AxisStyleModel({
    name: LineStyleEnum.solid,
    icon: 'solid',
  }),
  new AxisStyleModel({
    name: LineStyleEnum.dotted,
    icon: 'dotted',
  }),
  new AxisStyleModel({
    name: LineStyleEnum.dashed,
    icon: 'dashed',
  }),
];
export const AxisPointerTypes = [
  new AxisStyleModel({
    name: AxisPointerEnum.line,
    icon: 'line',
  }),
  new AxisStyleModel({
    name: AxisPointerEnum.none,
    icon: 'none',
  }),
  new AxisStyleModel({
    name: AxisPointerEnum.shadow,
    icon: 'shadow',
  }),
];

export const NameLocationTypes = [
  new AxisStyleModel({
    name: NameLocationEnum.start,
    icon: 'arrow-down',
  }),
  new AxisStyleModel({
    name: NameLocationEnum.middle,
    icon: 'arrows-alt-v',
  }),
  new AxisStyleModel({
    name: NameLocationEnum.end,
    icon: 'arrow-up',
  }),
];

export const AxisSymbolTypes = [
  new AxisStyleModel({
    name: AxisSymbolEnum.arrow,
    icon: '',
  }),
  new AxisStyleModel({
    name: AxisSymbolEnum.none,
    icon: '',
  }),
];

export interface AxisTypeInterface {
  value: AxisTypeEnum;
  icon: IconName;
  description: string;
}

export const AxisTypes: AxisTypeInterface[] = [
  {
    value: AxisTypeEnum.category,
    icon: 'list',
    description: 'For categorical dimensions with no order',
  },
  {
    value: AxisTypeEnum.log,
    icon: 'analytics',
    description: 'For numeric dimensions -  displaying data over a very wide range of values in a compact way',
  },
  {
    value: AxisTypeEnum.value,
    icon: 'sort-numeric-up-alt',
    description: 'For numeric dimensions - auto-sort and auto-fill gaps',
  },
  {
    value: AxisTypeEnum.time,
    icon: 'calendar-alt',
    description: 'For date dimensions - auto-sort and auto-fill gaps',
  },
];

export const FontFamilyTypes = ['Arial', 'Calibri', 'Candara'];
